(() => {
  const instances = new WeakMap();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const initGallery = (section, cleanups) => {
    const track = section.querySelector('[data-product-track]');
    const slides = [...section.querySelectorAll('[data-product-slide]')];
    const thumbnails = [...section.querySelectorAll('[data-product-thumbnail]')];
    if (!(track instanceof HTMLElement) || !slides.length) return;

    const setActiveMedia = (index) => {
      thumbnails.forEach((thumbnail) => {
        const isActive = Number(thumbnail.dataset.mediaIndex) === index;
        thumbnail.setAttribute('aria-current', String(isActive));
        thumbnail.classList.toggle('on', isActive);
      });
    };

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        const targetId = thumbnail.dataset.targetId;
        const target = targetId ? document.getElementById(targetId) : null;
        if (!target) return;
        target.scrollIntoView({
          behavior: reduceMotion.matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'start'
        });
        setActiveMedia(Number(thumbnail.dataset.mediaIndex || 0));
      });
    });

    if ('IntersectionObserver' in window && thumbnails.length) {
      const observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((left, right) => right.intersectionRatio - left.intersectionRatio)[0];
          if (!visible) return;
          setActiveMedia(Number(visible.target.dataset.mediaIndex || 0));
        },
        { root: track, threshold: [0.55, 0.75] }
      );
      slides.forEach((slide) => observer.observe(slide));
      cleanups.push(() => observer.disconnect());
    }

    const dialog = section.querySelector('[data-product-dialog]');
    const dialogImage = section.querySelector('[data-product-dialog-image]');
    const closeButton = section.querySelector('[data-product-dialog-close]');
    const previousButton = section.querySelector('[data-product-dialog-previous]');
    const nextButton = section.querySelector('[data-product-dialog-next]');
    const counter = section.querySelector('[data-product-dialog-counter]');
    const dialogThumbs = [...section.querySelectorAll('[data-product-dialog-thumbnail]')];
    const lightboxLinks = [...section.querySelectorAll('[data-product-lightbox-open]')];
    let opener = null;
    let activeLightboxIndex = 0;

    if (dialog instanceof HTMLDialogElement && dialogImage instanceof HTMLImageElement) {
      const getMediaIndex = (link) => link.dataset.lightboxMediaIndex || link.closest('[data-product-slide]')?.dataset.mediaIndex || '0';
      const getEntryIndex = (mediaIndex) => lightboxLinks.findIndex((link) => getMediaIndex(link) === String(mediaIndex));
      const counterTemplate = dialog.dataset.counterTemplate || '__INDEX__ / __TOTAL__';

      const updateCounter = () => {
        if (!(counter instanceof HTMLElement)) return;
        counter.textContent = counterTemplate
          .replace('__INDEX__', String(activeLightboxIndex + 1))
          .replace('__TOTAL__', String(lightboxLinks.length));
      };

      const updateLightbox = (index) => {
        if (!lightboxLinks.length) return;
        activeLightboxIndex = (index + lightboxLinks.length) % lightboxLinks.length;
        const link = lightboxLinks[activeLightboxIndex];
        if (!(link instanceof HTMLAnchorElement)) return;

        dialogImage.src = link.href;
        dialogImage.alt = link.dataset.lightboxAlt || '';
        dialogImage.setAttribute('aria-describedby', 'ProductDialogCounter-' + section.id);
        setActiveMedia(Number(getMediaIndex(link)) || 0);
        updateCounter();

        dialogThumbs.forEach((thumbnail) => {
          const isActive = thumbnail.dataset.lightboxMediaIndex === getMediaIndex(link);
          thumbnail.setAttribute('aria-selected', String(isActive));
          thumbnail.classList.toggle('is-active', isActive);
        });
      };

      const openLightbox = (link) => {
        if (!(link instanceof HTMLAnchorElement) || typeof dialog.showModal !== 'function') return;
        const nextIndex = lightboxLinks.indexOf(link);
        if (nextIndex < 0) return;
        opener = link;
        updateLightbox(nextIndex);
        dialog.showModal();
        if (closeButton instanceof HTMLElement) closeButton.focus();
      };

      lightboxLinks.forEach((link) => {
        link.addEventListener('click', (event) => {
          if (typeof dialog.showModal !== 'function') return;
          event.preventDefault();
          openLightbox(link);
        });
      });

      if (closeButton instanceof HTMLButtonElement) {
        closeButton.addEventListener('click', () => dialog.close());
      }

      if (previousButton instanceof HTMLButtonElement) {
        previousButton.addEventListener('click', () => updateLightbox(activeLightboxIndex - 1));
      }

      if (nextButton instanceof HTMLButtonElement) {
        nextButton.addEventListener('click', () => updateLightbox(activeLightboxIndex + 1));
      }

      dialogThumbs.forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => {
          const nextIndex = getEntryIndex(thumbnail.dataset.lightboxMediaIndex || '0');
          if (nextIndex >= 0) updateLightbox(nextIndex);
        });
      });

      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          updateLightbox(activeLightboxIndex - 1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          updateLightbox(activeLightboxIndex + 1);
        }
      });

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });

      dialog.addEventListener('close', () => {
        dialogImage.removeAttribute('src');
        dialogImage.removeAttribute('aria-describedby');
        if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
        opener = null;
      });

      if (counter instanceof HTMLElement) counter.id = 'ProductDialogCounter-' + section.id;

      cleanups.push(() => {
        if (dialog.open) dialog.close();
        dialogImage.removeAttribute('src');
      });
    }
  };

  const initProductForm = (section) => {
    const radios = [...section.querySelectorAll('[data-variant-radio]')];
    const sellingPlanRadios = [...section.querySelectorAll('[data-selling-plan-radio]')];
    const price = section.querySelector('[data-product-price]');
    const comparePrice = section.querySelector('[data-product-compare-price]');
    const sale = section.querySelector('[data-product-sale]');
    const saleLabel = section.querySelector('[data-product-sale-label]');
    const unitPrice = section.querySelector('[data-product-unit-price]');
    const submitButton = section.querySelector('[data-product-submit]');
    const submitLabel = section.querySelector('[data-product-submit-label]');
    const submitArrow = section.querySelector('[data-product-submit-arrow]');
    const quantityInput = section.querySelector('[data-product-quantity]');
    const oneTimePrice = section.querySelector('[data-one-time-price]');
    const stickyPrice = section.querySelector('[data-product-sticky-price]');
    const stickyComparePrice = section.querySelector('[data-product-sticky-compare]');
    let activeVariant = null;

    const syncQuickAddForms = (variant) => {
      const selectedPlan = sellingPlanRadios.find((radio) => radio.checked && !radio.disabled && radio.value);
      const productId = section.dataset.productId;

      document.querySelectorAll('[data-quick-add-form]').forEach((form) => {
        if (!(form instanceof HTMLFormElement) || form.dataset.quickAddProductId !== productId) return;

        const variantInput = form.querySelector('[data-quick-add-variant]');
        const quantity = form.querySelector('[data-quick-add-quantity]');
        const sellingPlan = form.querySelector('[data-quick-add-selling-plan]');
        const button = form.querySelector('[data-quick-add-submit]');

        if (variantInput instanceof HTMLInputElement) variantInput.value = variant.value;
        if (quantity instanceof HTMLInputElement) quantity.value = variant.dataset.quantityMin || '1';
        if (sellingPlan instanceof HTMLInputElement) {
          sellingPlan.value = selectedPlan?.value || '';
          sellingPlan.disabled = !selectedPlan;
        }
        if (button instanceof HTMLButtonElement) {
          button.dataset.available = variant.dataset.available || 'false';
          button.disabled = button.dataset.available !== 'true';
        }
      });
    };

    const updatePrice = () => {
      if (!(activeVariant instanceof HTMLInputElement)) return;
      const selectedPlan = sellingPlanRadios.find((radio) => radio.checked && !radio.disabled && radio.value);
      const currentPrice = selectedPlan?.dataset.price || activeVariant.dataset.price || '';
      if (price) price.textContent = currentPrice;
      if (stickyPrice) stickyPrice.textContent = currentPrice;
      if (comparePrice) {
        const compareValue = selectedPlan
          ? activeVariant.dataset.price || ''
          : activeVariant.dataset.comparePrice || '';
        comparePrice.textContent = compareValue;
        comparePrice.hidden = !compareValue;
        if (stickyComparePrice) {
          stickyComparePrice.textContent = compareValue;
          stickyComparePrice.hidden = !compareValue;
        }
      }
      const salePercentage = selectedPlan
        ? selectedPlan.dataset.salePercentage || ''
        : activeVariant.dataset.salePercentage || '';
      if (sale instanceof HTMLElement) sale.hidden = !salePercentage;
      if (saleLabel instanceof HTMLElement) {
        saleLabel.textContent = (sale?.dataset.saleLabelTemplate || '__PERCENT__')
          .replace('__PERCENT__', salePercentage);
      }
      if (unitPrice instanceof HTMLElement) {
        const unitPriceValue = activeVariant.dataset.unitPrice || section.dataset.unitPriceFallback || '';
        unitPrice.textContent = unitPriceValue;
        unitPrice.hidden = !unitPriceValue;
      }
    };

    const updatePurchaseUrl = () => {
      if (!(activeVariant instanceof HTMLInputElement)) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', activeVariant.value);
      const selectedPlan = sellingPlanRadios.find((radio) => radio.checked && !radio.disabled && radio.value);
      if (selectedPlan) url.searchParams.set('selling_plan', selectedPlan.value);
      else url.searchParams.delete('selling_plan');
      window.history.replaceState({}, '', url);
    };

    const syncSellingPlans = (variant) => {
      if (!sellingPlanRadios.length) return;
      const requiresSellingPlan = variant.dataset.requiresSellingPlan === 'true';

      sellingPlanRadios.forEach((planRadio) => {
        const option = planRadio.closest('[data-selling-plan-option]');
        const isOneTime = planRadio.value === '';
        const isAvailable = isOneTime
          ? !requiresSellingPlan
          : planRadio.dataset.variantId === variant.value;
        planRadio.disabled = !isAvailable;
        if (option instanceof HTMLElement) option.hidden = !isAvailable;
      });

      const selectedPlan = sellingPlanRadios.find((planRadio) => planRadio.checked && !planRadio.disabled);
      if (!selectedPlan) {
        const fallback = sellingPlanRadios.find((planRadio) => !planRadio.disabled && planRadio.value)
          || sellingPlanRadios.find((planRadio) => !planRadio.disabled);
        if (fallback) fallback.checked = true;
      }
    };

    const updateQuantityRule = (radio) => {
      if (!(quantityInput instanceof HTMLInputElement)) return;
      const minimum = radio.dataset.quantityMin || '1';
      const increment = radio.dataset.quantityIncrement || '1';
      quantityInput.min = minimum;
      quantityInput.step = increment;

      if (radio.dataset.quantityMax) quantityInput.max = radio.dataset.quantityMax;
      else quantityInput.removeAttribute('max');

      const current = Number(quantityInput.value);
      const minimumValue = Number(minimum);
      const incrementValue = Number(increment);
      const maximumValue = Number(radio.dataset.quantityMax);
      const exceedsMaximum = radio.dataset.quantityMax && current > maximumValue;
      const followsIncrement = Number.isInteger((current - minimumValue) / incrementValue);

      if (!Number.isFinite(current) || current < minimumValue || exceedsMaximum || !followsIncrement) {
        quantityInput.value = minimum;
      }
    };

    const applyVariant = (radio, updateUrl) => {
      if (!(radio instanceof HTMLInputElement)) return;
      const available = radio.dataset.available === 'true';
      activeVariant = radio;
      if (oneTimePrice) oneTimePrice.textContent = radio.dataset.price || '';
      syncSellingPlans(radio);
      updatePrice();
      syncQuickAddForms(radio);

      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = !available;
        if (submitLabel instanceof HTMLElement) {
          submitLabel.textContent = available
            ? section.dataset.addToCartText || ''
            : section.dataset.soldOutText || '';
        }
        if (submitArrow instanceof HTMLElement) submitArrow.hidden = !available;
      }

      updateQuantityRule(radio);
      if (quantityInput instanceof HTMLInputElement) quantityInput.disabled = !available;

      if (updateUrl) {
        updatePurchaseUrl();
      }
    };

    radios.forEach((radio) => {
      if (radio.type !== 'radio') return;
      radio.addEventListener('change', () => {
        if (radio.checked) applyVariant(radio, true);
      });
    });

    sellingPlanRadios.forEach((planRadio) => {
      planRadio.addEventListener('change', () => {
        if (!planRadio.checked) return;
        updatePrice();
        if (activeVariant) syncQuickAddForms(activeVariant);
        updatePurchaseUrl();
      });
    });

    const checked = radios.find((radio) => radio.type !== 'radio' || radio.checked) || radios[0];
    if (checked) applyVariant(checked, false);

  };

  const initStickyBar = (section, cleanups) => {
    const stickyBar = section.querySelector('[data-product-sticky]');
    if (!(stickyBar instanceof HTMLElement)) return;

    const isClearOfFooterDisclaimer = () => {
      const disclaimer = document.querySelector('[data-footer-disclaimer]');
      if (!(disclaimer instanceof HTMLElement)) return true;

      const disclaimerBounds = disclaimer.getBoundingClientRect();
      return disclaimerBounds.bottom <= 0 || disclaimerBounds.top >= window.innerHeight;
    };

    if (stickyBar.hasAttribute('data-index-product-sticky')) {
      const comparisonSection = document.querySelector('[data-index-sticky-keep]');
      const updateIndexVisibility = () => {
        const sectionBounds = section.getBoundingClientRect();
        const comparisonBounds = comparisonSection?.getBoundingClientRect();
        const pastHero = window.scrollY > 120;
        const productVisible = sectionBounds.top < window.innerHeight * 0.85 && sectionBounds.bottom > 0;
        const comparisonVisible = Boolean(comparisonBounds && comparisonBounds.top < window.innerHeight && comparisonBounds.bottom > 0);
        const isVisible = pastHero && (!productVisible || comparisonVisible) && isClearOfFooterDisclaimer();
        stickyBar.classList.toggle('is-visible', isVisible);
        stickyBar.classList.toggle('show', isVisible);
      };

      updateIndexVisibility();
      window.addEventListener('scroll', updateIndexVisibility, { passive: true });
      window.addEventListener('resize', updateIndexVisibility);
      cleanups.push(() => {
        window.removeEventListener('scroll', updateIndexVisibility);
        window.removeEventListener('resize', updateIndexVisibility);
      });
      return;
    }

    const sectionWrapper = section.closest('.shopify-section') || section.parentElement;
    const nextSection = sectionWrapper?.nextElementSibling;
    const trigger = nextSection?.querySelector('[data-product-sticky-trigger]');
    const observeTarget = trigger || section;
    const setVisibility = (isVisible) => {
      const shouldShow = isVisible && isClearOfFooterDisclaimer();
      stickyBar.classList.toggle('is-visible', shouldShow);
      stickyBar.classList.toggle('show', shouldShow);
    };
    const updateVisibility = () => {
      const bounds = observeTarget.getBoundingClientRect();
      setVisibility(trigger ? bounds.top <= window.innerHeight : bounds.bottom <= 0);
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          const isVisible = trigger
            ? entry.isIntersecting || entry.boundingClientRect.top <= 0
            : !entry.isIntersecting;
          setVisibility(isVisible);
        },
        { threshold: 0 }
      );
      observer.observe(observeTarget);
      cleanups.push(() => observer.disconnect());
    }

    updateVisibility();
    window.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);
    cleanups.push(() => {
      window.removeEventListener('scroll', updateVisibility);
      window.removeEventListener('resize', updateVisibility);
    });
  };

  const initSupplementFactsDialog = (section, cleanups) => {
    const dialog = section.querySelector('[data-supplement-dialog]');
    const closeButton = section.querySelector('[data-supplement-dialog-close]');
    if (!(dialog instanceof HTMLDialogElement)) return;

    const close = () => {
      if (dialog.open) dialog.close();
    };

    section.querySelectorAll('[data-supplement-dialog-open]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        if (typeof dialog.showModal !== 'function') return;
        dialog.showModal();
        dialog.focus();
      });
    });

    closeButton?.addEventListener('click', close);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });

    cleanups.push(() => {
      close();
    });
  };

  const initMainProduct = (scope = document) => {
    scope.querySelectorAll('[data-main-product]').forEach((section) => {
      if (instances.has(section)) return;
      const cleanups = [];
      initGallery(section, cleanups);
      initProductForm(section);
      initStickyBar(section, cleanups);
      initSupplementFactsDialog(section, cleanups);
      instances.set(section, () => cleanups.forEach((cleanup) => cleanup()));
    });
  };

  const unloadMainProduct = (scope) => {
    scope.querySelectorAll('[data-main-product]').forEach((section) => {
      const cleanup = instances.get(section);
      if (cleanup) cleanup();
      instances.delete(section);
    });
  };

  document.addEventListener('DOMContentLoaded', () => initMainProduct());
  document.addEventListener('shopify:section:load', (event) => initMainProduct(event.target));
  document.addEventListener('shopify:section:unload', (event) => unloadMainProduct(event.target));

  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    const group = details.closest('.accs, .faq-list');
    if (!group) return;
    group.querySelectorAll('details[open]').forEach((other) => {
      if (other !== details) other.open = false;
    });
  }, true);
})();
